<template>
  <div v-if="showMessenger">
    <el-popover
      :visible="isOpen"
      trigger="click"
      placement="bottom-end"
      popper-class="messenger tw-overflow-hidden"
      :width="458"
      @show="$store.commit('v2/admin/messaging/set', ['openedThreadData', { id: null, foreignerId: null }])"
      @hide="isNewThreadDrawerOpen = false"
    >
      <template #reference>
        <div class="tw-fixed tw-bottom-10 tw-right-10">
          <i-badge
            v-if="countUnseenThreads"
            type="primary"
            is-circle
            class="tw-absolute tw-z-10 tw--top-1.5 tw--right-1.5 tw-border-2 tw-border-white dark:tw-border-dm"
          >
            {{ countUnseenThreads }}
          </i-badge>
          <i-button
            type="primary"
            is-icon
            @click="isOpen = !isOpen"
          >
            <icon-close
              v-if="isOpen"
              class="tw-pointer-events-none"
            />
            <icon-message
              v-else
              class="tw-pointer-events-none"
            />
          </i-button>
        </div>
      </template>

      <div>
        <header class="tw-px-6 tw-py-5 tw-bg-light dark:tw-bg-dm-light">
          <div class="tw-flex tw-flex-col tw-gap-3">
            <div class="tw-flex tw-items-center tw-gap-3">
              <template v-if="openedThreadData.id">
                <i-button
                  type="white"
                  size="medium"
                  is-icon
                  @click="$store.commit('v2/admin/messaging/set', ['openedThreadData', { id: null, foreignerId: null }])"
                >
                  <icon-angle-left />
                </i-button>
              </template>
              <template v-else-if="!foreigner" />
              <template v-else>
                <ForeignerFamilyPopoverList
                  :family="foreigner?.family"
                  :foreigner-id="foreigner?.id"
                  show-messages-count
                  :redirect-on-family-member-click="false"
                  class="tw-leading-[0px]"
                  store-path="v2/admin/messaging"
                  @family-member-click="handleFamilyMemberChange"
                >
                  <i-user-avatar
                    :avatar="foreigner.avatar_url && foreigner.avatar_url['128'] && `${$baseHost}/${foreigner.avatar_url['128']}`"
                    :fullname="`${foreigner?.first_name} ${foreigner?.last_name}`"
                    size="mini"
                    class="tw-cursor-pointer"
                  />
                </ForeignerFamilyPopoverList>
              </template>
              <ForeignersMessagingFilterSearch
                v-if="openedThreadData.id"
                class="tw-flex-1"
              />
              <ForeignersMessagingFulltextSearch
                v-else
                class="tw-flex-1"
              />
              <template v-if="openedThreadData.id">
                <i-button
                  type="white"
                  size="medium"
                  is-icon
                  :is-active="isFiltersOpen"
                  @click="isFiltersOpen = !isFiltersOpen"
                >
                  <icon-filters />
                </i-button>
              </template>
              <router-link
                :to="`/foreigners/${foreigner?.id ?? 'preview'}/messaging`"
              >
                <i-button
                  type="white"
                  size="medium"
                  is-icon
                >
                  <icon-fullscreen />
                </i-button>
              </router-link>
            </div>
            <span
              v-if="openedThreadData.id && openedThread"
              class="tw-leading-4 tw-text-center tw-font-medium tw-cursor-pointer hover:tw-underline"
              @click="navigateToForeignerPage"
            >
              {{ openedThread.foreigner_fullname }}
            </span>
          </div>
          <ForeignersMessagingFilters
            v-if="isFiltersOpen && openedThreadData.id"
            class="tw-pt-3"
          />
        </header>
        <div>
          <ForeignersMessagingMessages
            v-if="openedThreadData.id"
            :permit-id="openedThreadData.id"
            :show-permit="openedThreadData.id === 'all'"
            :show-send-message="openedThreadData.id !== 'all'"
            :foreigner-id="openedThread?.foreigner_id"
            :are-filters-expanded="isFiltersOpen"
          />
          <div
            v-else
            ref="ForeignersMessagingThreads"
            class="ForeignersMessagingThreads tw-px-6 tw-divide-y tw-divide-gray dark:tw-divide-dm-gray tw-overflow-auto"
            :class="[
              showAllThreadsButton ? 'has-all-threads-button' : '',
            ]"
            @scroll="handleScroll"
          >
            <template v-if="fulltextSearchValue.length">
              <ForeignersMessagingThread
                v-for="message in $store.state.v2.admin.messaging.fulltextSearchResults"
                :key="message.id"
                :thread="message"
                :is-active="message.id === $store.state.v2.admin.messaging.fulltextMessageId"
                @click.native="() => {
                  $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(message.permit_id), foreignerId: message.foreigner_id }]);
                  $store.commit('v2/admin/messaging/set', ['fulltextMessageId', message.id]);
                }"
              />
            </template>

            <template v-else>
              <div
                v-if="threads.length === 0"
                class="tw-flex tw-h-full tw-justify-center tw-items-center"
              >
                {{ $t('messaging.no_threads') }}
              </div>
              <ForeignersMessagingThread
                v-for="thread in threads"
                :key="thread.id"
                :thread="thread"
                @click.native="() => $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(thread.permit_id),foreignerId: thread.foreigner_id}])"
              />
            </template>
            <div
              v-if="moreThreadsUrl"
              v-loading="true"
              class="tw-w-full tw-h-14"
            />
          </div>
        </div>
        <i-button
          v-if="foreigner && !openedThreadData.id"
          class="tw-flex tw-items-center tw-justify-center tw-w-full tw-rounded-t-none"
          @click="() => {
            $store.commit('v2/admin/messaging/set', ['openedThreadData', { foreignerId: foreigner.id, id: 'all' }]);
            $store.commit('v2/admin/messaging/set', ['fulltextSearchValue', '']);
            $store.commit('v2/admin/messaging/set', ['fulltextMessageId', null]);
          }"
        >
          <span class="tw-font-bold">
            {{ $t('messaging.all_threads') }}
          </span>
        </i-button>
        <i-button
          v-if="showAllThreadsButton"
          class="tw-absolute tw-bottom-16 tw-right-2 tw-shadow tw-border-none"
          is-icon
          type="white"
          @click="isNewThreadDrawerOpen = true"
        >
          <icon-plus />
        </i-button>
        <ForeignersNewThreadDrawer
          :is-open="isNewThreadDrawerOpen"
          store-path="v2/admin/messaging"
          :can-be-opened-threads="canBeOpenedThreads"
          @close="isNewThreadDrawerOpen = false"
        />
      </div>
    </el-popover>
  </div>
</template>

<script>
import ForeignerFamilyPopoverList from '@/components/Foreigner/ForeignerFamilyPopoverList.vue';
import ForeignersMessagingFilters from '@/components/Foreigners/Messaging/ForeignersMessagingFilters.vue';
import ForeignersMessagingMessages from '@/components/Foreigners/Messaging/ForeignersMessagingMessages.vue';
import ForeignersMessagingThread from '@/components/Foreigners/Messaging/ForeignersMessagingThread.vue';
import ForeignersMessagingFilterSearch from '@/components/Foreigners/Messaging/ForeignersMessagingFilterSearch.vue';
import ForeignersMessagingFulltextSearch from '@/components/Foreigners/Messaging/ForeignersMessagingFulltextSearch.vue';
import { mapGetters, mapState } from 'vuex';
import ForeignersNewThreadDrawer from '@/components/Foreigners/Messaging/ForeignersNewThreadDrawer.vue';

export default {
  components: {
    ForeignersNewThreadDrawer,
    ForeignerFamilyPopoverList,
    ForeignersMessagingFilters,
    ForeignersMessagingMessages,
    ForeignersMessagingThread,
    ForeignersMessagingFilterSearch,
    ForeignersMessagingFulltextSearch,
  },
  data() {
    return {
      isOpen: false,
      isFiltersOpen: false,
      timer: null,
      isNewThreadDrawerOpen: false,
      isMoreThreadsLoading: false,
    };
  },

  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      foreigner: (state) => state.v2.admin.messaging.foreigner,
      foreignerDataFromForeignerPage: (state) => state.v2.admin.foreigners.foreigner.foreigner,
      moreThreadsUrl: (state) => state.v2.admin.messaging.moreThreadsUrl,
      openedThreadData: (state) => state.v2.admin.messaging.openedThreadData,
      fulltextSearchValue: (state) => state.v2.admin.messaging.fulltextSearchValue,
    }),
    ...mapGetters({
      threads: 'v2/admin/messaging/threads',
      canBeOpenedThreads: 'v2/admin/messaging/canBeOpenedThreads',
      openedThread: 'v2/admin/messaging/getOpenedThreadPermit',
    }),
    countUnseenThreads() {
      return this.threads.filter(({ unseen_messages }) => unseen_messages).length;
    },
    showMessenger() {
      if (this.$isInRole(['HR', 'AGENCY'])) {
        return !this.me.features.hide_messaging_for_hr;
      }
      return this.$isInRole(['ADMIN', 'CONSULTANT']);
    },
    showAllThreadsButton() {
      return this.foreigner && !this.openedThreadData.id && this.canBeOpenedThreads.length !== 0;
    },
    isForeignerPage() {
      return this.$route.params.fid != null;
    },
  },
  watch: {
    foreignerDataFromForeignerPage(newForeigner) {
      if (newForeigner) {
        this.$store.commit('v2/admin/messaging/set', ['foreigner', newForeigner]);
        this.$store.dispatch('v2/admin/messaging/loadForeignerPermits', newForeigner.id);
        this.loadThreads();
      }
    },
    'openedThread.foreigner_id'(newThreadForeignerId) {
      if (newThreadForeignerId) {
        this.$store.dispatch('v2/admin/messaging/loadMessagingUsers', { foreignerId: newThreadForeignerId });
      }
    },
  },
  unmounted() {
    this.$store.commit('v2/admin/messaging/set', ['foreigner', null]);
  },

  mounted() {
    if (this.foreignerDataFromForeignerPage) {
      this.$store.commit('v2/admin/messaging/set', ['foreigner', this.foreignerDataFromForeignerPage]);
      this.$store.dispatch('v2/admin/messaging/loadForeignerPermits', this.foreignerDataFromForeignerPage.id);
      this.loadThreads();
    } else {
      this.handleFamilyMemberChange();
    }

    this.timer = setInterval(() => {
      if (this.foreigner) {
        this.loadThreads();
      }
    }, 30 * 1000);
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    this.isNewThreadDrawerOpen = false;
  },

  methods: {
    async loadThreads() {
      if (this.isForeignerPage && !this.foreigner?.id) {
        return;
      }
      await this.$store.dispatch('v2/admin/messaging/loadThreads', this.foreigner?.id);
    },
    async loadMoreThreads() {
      this.isMoreThreadsLoading = true;
      await this.$store.dispatch('v2/admin/messaging/loadMoreThreads');
      this.isMoreThreadsLoading = false;
    },
    handleScroll() {
      const container = this.$refs.ForeignersMessagingThreads;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight; // Adjust for visible height

      if (this.moreThreadsUrl && !this.isMoreThreadsLoading && distanceToBottom < 10) {
        this.loadMoreThreads();
      }
    },
    async handleFamilyMemberChange(foreignerId) {
      if (foreignerId) {
        await this.$store.dispatch('v2/admin/messaging/loadForeignerDetail', foreignerId);
        this.$store.dispatch('v2/admin/messaging/loadForeignerPermits', foreignerId);
      }
      await this.loadThreads();
    },
    navigateToForeignerPage() {
      this.$router.push({
        name: 'foreignersPermit',
        params: {
          fid: this.openedThread.foreigner_id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ForeignersMessagingThreads {
  height: 60vh;
  overscroll-behavior: none;

  &.has-all-threads-button {
    height: calc(60vh - 48px);
  }
}
</style>
